import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  Briefcase,
  Users, 
  Calendar, 
  MapPin,
  Clock,
  AlertTriangle,
  MessageSquare,
  ChevronRight,
  Search,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Operations Workspace
 * 
 * Contains: Clients, Scheduling, Visits, Coverage, Exceptions, Communications
 * 
 * Note: Clients are the people you SERVE (patients, residents, customers)
 * They belong here, not in People.
 */

const OPERATIONS_SECTIONS = [
  {
    id: 'clients',
    name: 'Clients',
    description: 'People you serve — patients, residents, customers',
    icon: Users,
    path: '/operations/clients',
    color: 'bg-blue-500',
    stats: { label: 'Active', value: '—' }
  },
  {
    id: 'scheduling',
    name: 'Scheduling',
    description: 'Create and manage visit schedules',
    icon: Calendar,
    path: '/operations/scheduling',
    color: 'bg-purple-500',
    stats: { label: 'This Week', value: '—' }
  },
  {
    id: 'visits',
    name: 'Visits',
    description: 'Today\'s visits and visit history',
    icon: MapPin,
    path: '/operations/visits',
    color: 'bg-emerald-500',
    stats: { label: 'Today', value: '—' }
  },
  {
    id: 'coverage',
    name: 'Coverage',
    description: 'Coverage gaps and assignments',
    icon: Clock,
    path: '/operations/coverage',
    color: 'bg-amber-500',
    stats: { label: 'Gaps', value: '—' }
  },
  {
    id: 'exceptions',
    name: 'Exceptions',
    description: 'Issues that need attention',
    icon: AlertTriangle,
    path: '/operations/exceptions',
    color: 'bg-red-500',
    stats: { label: 'Pending', value: '—' }
  },
  {
    id: 'communications',
    name: 'Communications',
    description: 'Messages and notifications',
    icon: MessageSquare,
    path: '/operations/communications',
    color: 'bg-pink-500',
    stats: null
  },
];

export default function OperationsWorkspace() {
  const location = useLocation();
  const isLanding = location.pathname === '/operations';

  // If we're on a sub-route, render the Outlet
  if (!isLanding) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-[#0D1B33]">Operations</h1>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input 
                  type="text"
                  placeholder="Search operations..."
                  className="pl-9 pr-4 py-2 text-sm border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF] w-64"
                />
              </div>
              <Link to="/operations/scheduling/new">
                <Button className="bg-[#176DFF] hover:bg-[#176DFF]/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Visit
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-[#64748B]">
            Clients, scheduling, visits, and daily operations.
          </p>
        </header>

        {/* Section Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {OPERATIONS_SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#176DFF]/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${section.color}`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#176DFF] group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-semibold text-[#0D1B33] mb-1">{section.name}</h3>
              <p className="text-sm text-[#64748B] mb-3">{section.description}</p>
              {section.stats && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#64748B]">{section.stats.label}:</span>
                  <span className="font-semibold text-[#0D1B33]">{section.stats.value}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
